import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";

import api from "../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../utils/toastOptions";
import { authenticationService } from "../../../services/auth.service";
import CurrencyInput from "../../../components/CurrencyInput";
import { getValidator } from "../../../utils/validatorsYup";
import Modal from "../../../components/modal";
import { PersonPaymentType } from "../../../models/enums/PersonPaymentType";
import IPersonBilling from "../../../models/IPersonBilling";
import IOrderPaymentDTO from "../../../models/IOrderPaymentDTO";
import { PaymentType } from "../../../models/enums/PaymentType";

interface Params {
  payment?: IOrderPaymentDTO;
  open: boolean;
  afterHandleSubmit: any;
  afterCloseModal: any;
}

interface IPayment {
  id: number;
  paidAt: Date;
  paidAmount: number;
  paymentType?: PaymentType;
  note?: string;
}

export default function OrderPayment(props: Params) {
  const { payment, open, afterHandleSubmit, afterCloseModal } = props;

  const [modalIsOpen, setModelOpen] = useState(false);
  const [isReadOnly, setReadOnly] = useState<boolean>(true);  
  const [billing, setBilling] = useState<IPersonBilling>();
  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const schema = yup.object().shape({
    paidAmount: getValidator({isNumeric: true}).required('Valor obrigatório'),
    paymentType: yup.string().required("Tipo obrigatório")
  });

  const {
    register,
    handleSubmit,
    reset,
    errors,
    formState,
    setValue,
    clearErrors,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setReadOnly(authenticationService.isReadOnly());
    setModelOpen(open);

    if (!open) return;

    if (payment) {

      setBilling({
        id: payment.id,
        paidAt: new Date(),
        paidAmount: payment.paidValue,
      } as IPersonBilling);

      setIsDisabled(false);
    }

  }, [payment, open]);  

  const closeModal = () => {
    handleCloseModal();
    reset();
    afterCloseModal();
  };

  const handleCloseModal = () => {
    clearErrors();
    setModelOpen(false);
    afterCloseModal();
  };

  const onSubmit = async (data: IPayment) => {
    try {
      if (billing && payment) {
        payment.paidValue = data.paidAmount || 0;
        payment.paidAt = billing.paidAt;
        payment.paymentType = data.paymentType;

        await api.post(`/orders/${payment?.keyOrder}/payment`, payment);
        
        handleCloseModal();
        afterHandleSubmit(billing.paidAt);
      }
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="modal-small"
        shouldCloseOnOverlayClick={false}
      >
        <div className="uk-modal-header">
          <span className="uk-modal-title uk-text-large">Pagamento Pedido</span>
        </div>
        <button
          className="uk-modal-close-default"
          uk-close="true"
          type="button"
          onClick={handleCloseModal}
        />
        <form onReset={reset}
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            return handleSubmit<IPayment>(onSubmit)(e)
          }}
        >
          <div className="uk-modal-body">
            <div className="uk-grid-small" uk-grid="true">              
              <div className="uk-width-1-3@s">
                <CurrencyInput
                  name="paidAmount"
                  label="Valor"
                  placeholder="Valor"
                  defaultValue={billing?.paidAmount}
                  register={register}
                  setValue={setValue}
                  disabled={isDisabled}
                  trigger={trigger}
                  onChange={(value) => setBilling({...billing, paidAmount: value} as IPersonBilling)}
                  error={errors.paidAmount?.message}
                />
              </div>              
              <div className="uk-width-1-3@s">
                <div className="uk-form-controls">
                  <label className="uk-form-label" htmlFor="paymentType">Pago via</label>
                  <div
                    uk-form-custom="target: > * > span:first-child"
                    className="uk-width-1"
                  >
                    <select
                      className="uk-form-small"
                      id="paymentType"
                      name="paymentType"
                      defaultValue={payment?.paymentType}
                      ref={register}
                      onChange={(e) => {
                        setValue('paymentType', e.target.value);
                      }}
                    >
                      <option value="">Selecione...</option>
                      {
                        Object.keys(PersonPaymentType)
                          .map((key) => {
                            return (
                              <option key={key} value={PersonPaymentType[key as keyof typeof PersonPaymentType]}>
                                {PersonPaymentType[key as keyof typeof PersonPaymentType]}
                              </option>
                            );
                          })
                      }
                    </select>
                    <button
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{ textTransform: "none" }}
                      type="button"
                      tabIndex={-1}
                      disabled={isDisabled}
                    >
                      <span>{billing?.paymentType as PersonPaymentType}</span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
                <small className="uk-text-danger">{errors.paymentType?.message}</small>
              </div>           
            </div>
          </div>
          <div className="uk-modal-footer">
            <div
              className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
              uk-grid="true"
            >
              <button
                className="uk-button uk-button-small uk-button-default uk-modal-close uk-margin-left"
                onClick={handleCloseModal}
                type="button"
              >
                Cancelar
              </button>
              <button
                disabled={formState.isSubmitting || isReadOnly || isDisabled}
                className="uk-button uk-button-small uk-button-primary uk-margin-left"
                type="submit"
              >
                {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                Salvar
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}
